/**
 * Card — the visual primitive every left-panel section uses.
 *
 * Click anywhere on the header to toggle open/closed. The chevron at
 * the left signals state; the optional `meta` slot at the right shows
 * a one-line summary (e.g. "12 categories", "16 violations") so users
 * know what's behind a collapsed card without opening it.
 *
 * The card itself is dumb — it owns no business state, just the
 * open/closed flag. All four left-panel sections (Project Profile,
 * Requirements Matched, Applicable Requirements, Sources) share it,
 * as do the Violations and Quarantined-Rules sections. That's the
 * point: the left column reads as one stack of cards rather than
 * two physical panels stitched together.
 */
import type { JSX } from "preact";
import { useState } from "preact/hooks";

export interface CardProps {
  title: string;
  children: preact.ComponentChildren;
  /** Optional left-edge accent stripe color. */
  accent?: string;
  defaultOpen?: boolean;
  /** Right-aligned header hint, visible whether the card is open or closed. */
  meta?: preact.ComponentChildren;
  /** Subtle tint applied to the card background (e.g. for warnings). */
  tone?: "default" | "warning";
}

export function Card({
  title,
  children,
  accent,
  defaultOpen = true,
  meta,
  tone = "default",
}: CardProps): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);

  const background =
    tone === "warning" ? "rgba(214,83,83,0.06)" : "rgba(255,255,255,0.025)";

  return (
    <div
      style={{
        background,
        border: "1px solid var(--vera-border)",
        borderRadius: 8,
        padding: open ? "8px 12px 10px" : "8px 12px",
        borderLeft: accent ? `3px solid ${accent}` : undefined,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          width: "100%",
          background: "transparent",
          border: "none",
          padding: 0,
          margin: 0,
          display: "flex",
          alignItems: "center",
          gap: 8,
          cursor: "pointer",
          fontSize: 10,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          color: "var(--vera-text-dim)",
          marginBottom: open ? 8 : 0,
          fontFamily: "inherit",
        }}
      >
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 10,
            transition: "transform 120ms ease",
            transform: open ? "rotate(90deg)" : "rotate(0deg)",
            color: "var(--vera-text-dim)",
            fontSize: 9,
          }}
        >
          ▶
        </span>
        <span style={{ flex: 1, textAlign: "left" }}>{title}</span>
        {meta && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 500,
              color: "var(--vera-text-dim)",
              textTransform: "none",
              letterSpacing: 0,
            }}
          >
            {meta}
          </span>
        )}
      </button>
      {open && children}
    </div>
  );
}
